import { useAtom } from 'jotai';

import { useGetApiV1ExamExamidLangs, useGetApiV1ExamGridExamid } from '@/shared/api/generated';
import type { ExamLangListItemViewModel } from '@/shared/api/generated/types/ExamLangListItemViewModel';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import { type ExamEditTab, examEditTabAtom } from '../store/atoms/examEditAtom';

/**
 * Hook para carregar o detalhe de uma prova, seus idiomas e estado de abas.
 *
 * O objeto `exam` vem do grid filtrado por ID (endpoint de listagem).
 * Os `languages` são carregados diretamente do endpoint de idiomas da prova.
 * As questões são carregadas por idioma via `useExamQuestions`.
 *
 * @param id - ID da prova (string vindo de route params)
 * @returns Dados da prova, idiomas, aba ativa e estado de loading
 *
 * @example
 * ```tsx
 * const { exam, languages, activeTab, setActiveTab, isLoading } = useExamDetail(id);
 * ```
 */
export function useExamDetail(id: string) {
  const [activeTab, setActiveTab] = useAtom(examEditTabAtom);
  const numId = Number(id);

  const {
    data: examData,
    isError: isErrorExam,
    isLoading: isLoadingExam,
  } = useGetApiV1ExamGridExamid(Number(id));

  const {
    data: langData,
    isError: isErrorLang,
    isLoading: isLoadingLang,
  } = useGetApiV1ExamExamidLangs(numId);

  return {
    exam: examData,
    languages: unwrapPagedResponse<ExamLangListItemViewModel>(langData),
    questions: [] as never[],
    activeTab: activeTab as ExamEditTab,
    setActiveTab: (tab: ExamEditTab) => setActiveTab(tab),
    isLoading: isLoadingExam || isLoadingLang,
    isError: isErrorExam || isErrorLang,
  } as const;
}
